import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ProductCreateRequest,
  ProductDetail,
  ProductUpdateRequest,
} from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiMeta,
} from '@/lib/http-core';
import type { ApiError as ApiErrorPayload } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import { productQueryKey } from './useProductQuery';
import { productOptionGroupsQueryKey } from './useProductOptionGroupsQuery';

/**
 * 상품이 바뀐 뒤 무효화할 목록 — 상품 목록과 **카테고리 쪽 캐시 전부**.
 *
 * catalog(release-qa/v1.0.9~)는 상품 등록·수정·삭제·복사·엑셀 일괄변경에서 카테고리가 바뀌면
 * category_product_map 을 같은 트랜잭션에서 맞춘다(뺀 카테고리 행 삭제, 추가된 카테고리는 맨 뒤에 추가).
 * 그래서 "카테고리 관리 > 상품 매핑"(['admin', 'categories', id, 'products'])도 함께 낡는다 — 상품 화면에서
 * 카테고리를 뺀 직후 매핑 화면을 열면 예전 목록이 보이던 이유다. 접두어 ['admin', 'categories'] 로
 * 카테고리 트리와 모든 매핑 목록을 한 번에 무효화한다(보고 있지 않은 쿼리는 다음에 열 때 다시 받는다).
 */
export const invalidateProductLists = (qc: ReturnType<typeof useQueryClient>): Promise<void> =>
  Promise.all([
    qc.invalidateQueries({ queryKey: ['admin', 'products'] }),
    qc.invalidateQueries({ queryKey: ['admin', 'categories'] }),
  ]).then(() => undefined);

const invalidateLists = invalidateProductLists;

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, { status, ...(env.meta ? { meta: env.meta } : {}) });
  }
  throw new TransportError(`Mutation failed with status ${status}`, raw);
};

export const useCreateProductMutation = () => {
  const qc = useQueryClient();
  return useMutation<ProductDetail, Error, ProductCreateRequest>({
    mutationFn: async (body) => {
      const idempotencyKey = idempotencyKeyFor('POST', '/admin/v1/products', body);
      const { data, error, response } = await apiClient.raw.POST('/admin/v1/products', {
        body,
        params: { header: { 'Idempotency-Key': idempotencyKey } },
      });
      if (!data || data.success !== true) throwIfError(error ?? data, response.status);
      return (data as { data: ProductDetail }).data;
    },
    onSuccess: () => invalidateLists(qc),
  });
};

export interface UpdateProductInput {
  productId: string;
  body: ProductUpdateRequest;
}

export const useUpdateProductMutation = () => {
  const qc = useQueryClient();
  return useMutation<ProductDetail, Error, UpdateProductInput>({
    mutationFn: async ({ productId, body }) => {
      const idempotencyKey = idempotencyKeyFor(
        'PUT',
        `/admin/v1/products/${productId}`,
        body,
      );
      const { data, error, response } = await apiClient.raw.PUT(
        '/admin/v1/products/{productId}',
        {
          body,
          params: {
            path: { productId },
            header: { 'Idempotency-Key': idempotencyKey },
          },
        },
      );
      if (!data || data.success !== true) throwIfError(error ?? data, response.status);
      return (data as { data: ProductDetail }).data;
    },
    onSuccess: async (_data, { productId }) => {
      await qc.invalidateQueries({ queryKey: productQueryKey(productId) });
      // 결정④ — 조합옵션 reconcile 후 구조 행(groupId/choiceId)은 새 UUID 로 재생성된다.
      // 캐시를 무효화해 다음 편집 진입이 stale id 로 시드되지 않게 한다(BE DTO 재시드 계약).
      await qc.invalidateQueries({ queryKey: productOptionGroupsQueryKey(productId) });
      await invalidateLists(qc);
    },
  });
};

export const useDeleteProductMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (productId) => {
      const idempotencyKey = idempotencyKeyFor(
        'DELETE',
        `/admin/v1/products/${productId}`,
        null,
      );
      const { error, response } = await apiClient.raw.DELETE(
        '/admin/v1/products/{productId}',
        {
          params: {
            path: { productId },
            header: { 'Idempotency-Key': idempotencyKey },
          },
        },
      );
      if (!response.ok) throwIfError(error, response.status);
    },
    onSuccess: () => invalidateLists(qc),
  });
};
